import { Card } from '@clay-run/glaze/card';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { SkeletonText } from '@clay-run/glaze/skeleton';
import { RenderDelay } from '@clay-run/glaze/render-delay';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '@clay-run/glaze/description-list';
import { Text } from '@clay-run/glaze/text';
import { SCAN_STATUS_BADGE, SCAN_STATUS_LABEL, scanSourceLine, type Scan } from '../scan';
import ScanReview from './ScanReview';

/**
 * The active-scan detail panel. While a single-photo intake's row is still
 * `processing` (plan U5), the rep is looking at fields that don't exist yet,
 * so this renders skeletons rather than a wall of em-dashes that reads like
 * "extraction found nothing" (`RenderDelay` suppresses them entirely for a
 * fast extraction, so a sub-threshold resolve doesn't flash placeholder bars
 * on screen). Once the row leaves `processing`, this renders `ScanReview` —
 * the real blank-by-default review form (U6) — keyed by `scan.id` so
 * switching to a different scan remounts the form with fresh initial state
 * instead of reseeding an already-mounted one.
 *
 * Extracted out of `ViewSwitcher.tsx` (where it used to be defined inline)
 * once the unified Scans page (`ScansPage.tsx`, plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md) needed the exact
 * same wide/narrow master-detail panel `ViewSwitcher.tsx` already had.
 */
export default function ActiveScanPanel({ scan }: { scan: Scan }) {
  if (scan.status !== 'processing') {
    return <ScanReview key={scan.id} scan={scan} />;
  }

  return (
    <Card padded className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <RenderDelay>
          <SkeletonText size="md" className="w-40" />
        </RenderDelay>
        <StatusBadge status={SCAN_STATUS_BADGE[scan.status]} size="xs">
          {SCAN_STATUS_LABEL[scan.status]}
        </StatusBadge>
      </div>
      <Text size="xs" variant="tertiary">
        {scanSourceLine(scan)}
      </Text>
      <DescriptionList>
        <DescriptionTerm>Company</DescriptionTerm>
        <DescriptionDetails>
          <RenderDelay>
            <SkeletonText size="sm" />
          </RenderDelay>
        </DescriptionDetails>
        <DescriptionTerm>Email</DescriptionTerm>
        <DescriptionDetails>
          <RenderDelay>
            <SkeletonText size="sm" />
          </RenderDelay>
        </DescriptionDetails>
        <DescriptionTerm>LinkedIn</DescriptionTerm>
        <DescriptionDetails>
          <RenderDelay>
            <SkeletonText size="sm" />
          </RenderDelay>
        </DescriptionDetails>
      </DescriptionList>
    </Card>
  );
}
